import axiosInstance from '../lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { API_ENDPOINTS } from '@/config/config';
import { Family, normalizeFamily } from '@/types/families';
import { logError, logDebug } from '@/utils/errorHandler';

/**
 * Fetches all product families from the API
 * @returns Promise with array of families
 */
export const getFamilies = async (): Promise<Family[]> => {
  try {
    // Log both path configurations for debugging
    logDebug('Fetching families from configured endpoints', {
      pathsEndpoint: paths.families.root(),
      configEndpoint: API_ENDPOINTS.families.list,
    });

    // Use the endpoint from config
    const response = await axiosInstance.get(API_ENDPOINTS.families.list);
    const data = response.data;

    // Log raw response for debugging
    logDebug('Raw families data received', { data });

    // Normalize the data to ensure consistent format
    let families: Family[] = [];

    if (Array.isArray(data)) {
      families = data.map(family => normalizeFamily(family));
    } else if (data && typeof data === 'object') {
      // In case the API returns an object with families in a results property
      const results = data.results || data.items || [];
      families = Array.isArray(results) ? results.map(family => normalizeFamily(family)) : [];
    } else {
      logError(
        'Unexpected families data format',
        {
          component: 'familyService',
          action: 'getFamilies',
          metadata: { data, dataType: typeof data },
        },
        { showToast: false, userFacing: false }
      );
    }

    logDebug('Normalized families', { families, count: families.length });
    return families;
  } catch (error) {
    logError(
      'Failed to fetch families',
      {
        component: 'familyService',
        action: 'getFamilies',
        metadata: { error },
      },
      { showToast: false, userFacing: false }
    );
    return []; // Return empty array on error instead of throwing
  }
};

/**
 * Get a specific family by ID
 * @param id The family ID
 * @returns Promise with the family or null if not found
 */
export const getFamily = async (id: number): Promise<Family | null> => {
  try {
    // Use the endpoint from config
    const response = await axiosInstance.get(API_ENDPOINTS.families.update(id));
    return normalizeFamily(response.data);
  } catch (error) {
    logError(
      'Failed to fetch family by ID',
      {
        component: 'familyService',
        action: 'getFamily',
        metadata: { id, error },
      },
      { showToast: false, userFacing: false }
    );
    return null;
  }
};
